<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <template v-if="isCanvassingOrgAdmin || isCanvassingOrgFieldDirector || isGivarooStaff">
        <CanvassingOrgCampaignTable :campaigns="campaigns.list" />
      </template>
      <template v-else>
        <!-- Campaign list (mobile) -->
        <div class="md:hidden">
          <ul v-if="campaigns.list?.length" role="list" class="mt-2 space-y-3">
            <li
              v-for="(campaign, key) in campaigns.list"
              :key="key"
              class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
            >
              <p class="truncate text-sm font-semibold text-gray-900">
                {{ campaign.location ? campaign.name + ' ' + campaign.location : campaign.name }}
              </p>
              <p class="truncate text-xs text-gray-500">{{ campaign.npo.name }}</p>
              <div class="mt-3">
                <BaseButton
                  variant="secondary"
                  block
                  :icon="ArrowRightIcon"
                  icon-position="trailing"
                  :disabled="!donorFormIsActive(campaign)"
                  @click="handleDonorFormClick(campaign, false)"
                >
                  Donor Form
                </BaseButton>
              </div>
            </li>
          </ul>
          <p
            v-else
            class="mt-2 rounded-xl bg-white p-6 text-center text-sm text-gray-500 ring-1 ring-gray-200"
          >
            No results found.
          </p>
        </div>

        <!-- Campaign list (desktop) -->
        <div class="hidden md:block">
          <div class="mt-2 flex flex-col">
            <BaseTable
              :columns="campaignColumns"
              :rows="campaigns.list"
              empty-text="No results found."
            >
              <template #cell-name="{ row }">
                <span class="font-medium text-gray-900">
                  {{ row.location ? row.name + ' ' + row.location : row.name }}
                </span>
              </template>
              <template #cell-npo="{ row }">{{ row.npo.name }}</template>
              <template #cell-action="{ row }">
                <BaseButton
                  variant="secondary"
                  size="xs"
                  :icon="ArrowRightIcon"
                  icon-position="trailing"
                  :disabled="!donorFormIsActive(row)"
                  @click="handleDonorFormClick(row, true)"
                >
                  Donor Form
                </BaseButton>
              </template>
            </BaseTable>
          </div>
        </div>

        <BasePagination
          v-if="campaigns.list?.length"
          v-model="itemsPerPage"
          :page="campaigns.pagination.page"
          :total-pages="totalPages"
          :total-count="campaigns.pagination.totalCount"
          :display-start="displayStart"
          :display-end="displayEnd"
          :has-prev-page="campaigns.pagination.hasPrevPage"
          :has-next-page="campaigns.pagination.hasNextPage"
          @page-size-change="updateItemsPerPage"
          @prev="prevPage"
          @next="nextPage"
        />
      </template>
    </template>

    <Modal
      :headerText="locationModalHeader"
      :subheaderText="locationModalSubheader"
      ctaText="Close"
      :modalOpen="locationModalOpen"
      @closeModal="closeLocationModal"
    />
  </DashboardContainer>
</template>

<script>
import { onBeforeMount, ref, triggerRef, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import DashboardContainer from '@/components/DashboardContainer.vue'
import CanvassingOrgCampaignTable from '@/components/CanvassingOrgCampaignTable.vue'
import Pill from '@/components/Pill.vue'
import Spinner from '@/components/Spinner'
import Modal from '@/components/Modal'
import { ArrowRightIcon } from '@heroicons/vue/20/solid'
import { campaignFunctions } from '@/services/campaign/'
import GivarooSpinner from '@/components/GivarooSpinner.vue'

function requestGeolocationAccess() {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve({ ok: false, reason: 'unsupported' })
      return
    }

    navigator.geolocation.getCurrentPosition(
      () => resolve({ ok: true }),
      (error) => {
        resolve({
          ok: false,
          reason: error?.code === 1 ? 'denied' : error?.code === 3 ? 'timeout' : 'error',
        })
      },
      {
        enableHighAccuracy: false,
        // Without a timeout, getCurrentPosition can hang forever on devices that
        // never obtain a fix (e.g. Wi-Fi-only iPads), so neither callback fires
        // and the awaiting UI locks up silently. A timeout guarantees a callback.
        timeout: 10000,
        maximumAge: 60000,
      },
    )
  })
}

export default {
  name: 'campaigns',
  components: {
    DashboardContainer,
    ArrowRightIcon,
    Pill,
    Spinner,
    CanvassingOrgCampaignTable,
    GivarooSpinner,
    Modal,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const userToken = store.getters.user.token
    const isCanvassingOrgAdmin = store.getters.isCanvassingOrgAdmin
    const isCanvassingOrgFieldDirector = store.getters.isCanvassingOrgFieldDirector
    const isFundraiser = store.getters.isFundraiser
    const isGivarooStaff = store.getters.isGivarooStaff
    const { campaignCollection } = campaignFunctions()
    const campaigns = ref(campaignCollection)
    const loading = ref(false)
    const totalPages = ref()
    const itemsPerPage = ref(50)

    const locationModalOpen = ref(false)
    const locationModalReason = ref('denied')

    const locationModalHeader = computed(() =>
      locationModalReason.value === 'timeout'
        ? "Couldn't get your location"
        : 'Location sharing required',
    )

    const locationModalSubheader = computed(() =>
      locationModalReason.value === 'timeout'
        ? "We couldn't determine your location. Make sure location is enabled and you have a signal, then click Donor Form again."
        : 'Please turn on location on your device to access the donor form. Once enabled, click Donor Form again.',
    )

    const campaignColumns = [
      { key: 'name', label: 'Name', minWidth: '12rem' },
      { key: 'npo', label: 'NPO', minWidth: '10rem' },
      { key: 'action', label: '', align: 'right', minWidth: '9rem' },
    ]

    const user = computed(() => store.getters.user)

    const isMandatoryLocationEnabled = computed(
      () => isFundraiser && user.value?.canvassingOrg?.enableMandatoryLocation === true,
    )

    const donorFormIsActive = (campaign) => {
      return campaign.active && campaign.latestPaymentProvider && campaign.collectingDonations
    }

    // const leadFormIsActive = (campaign) => {
    //   return campaign.active && campaign.collectingLeads
    // }

    function donorFormHref(campaignId) {
      return router.resolve({
        name: 'CampaignDonate',
        params: { id: campaignId },
      }).href
    }

    function navigateToDonorForm(campaignId, newTab = false) {
      if (newTab) {
        window.open(donorFormHref(campaignId), '_blank')
      } else {
        router.push({ name: 'CampaignDonate', params: { id: campaignId } })
      }
    }

    async function handleDonorFormClick(campaign, newTab = false) {
      if (!donorFormIsActive(campaign)) return

      if (!isMandatoryLocationEnabled.value) {
        navigateToDonorForm(campaign.id, newTab)
        return
      }

      // When location is mandatory we must check it before navigating. That
      // check is asynchronous, and opening a new tab after an async gap is
      // unreliable across browsers (Safari blocks it, leaving a dead blank
      // tab). So we always navigate in the SAME tab for this flow: no tab is
      // opened until location is confirmed, and if it isn't we simply show the
      // message. This behaves identically on every device and browser.
      const result = await requestGeolocationAccess()

      if (result.ok) {
        navigateToDonorForm(campaign.id, false)
        return
      }

      locationModalReason.value = result.reason
      locationModalOpen.value = true
    }

    function closeLocationModal() {
      locationModalOpen.value = false
    }

    onBeforeMount(async () => {
      loading.value = true
      await store.dispatch('fetchUser')
      campaigns.value.pagination.size = itemsPerPage.value
      await campaigns.value.refresh().catch((error) => {
        alert(error)
      })
      totalPages.value = Math.ceil(campaigns.value.pagination.totalCount / itemsPerPage.value)
      triggerRef(campaigns)
      loading.value = false
    })
    const nextPage = async () => {
      if (campaigns.value.pagination.hasNextPage) {
        loading.value = true
        campaigns.value.pagination.size = itemsPerPage.value
        await campaigns.value.nextPage().catch((error) => {
          alert(error)
        })
        triggerRef(campaigns)
        totalPages.value = Math.ceil(campaigns.value.pagination.totalCount / itemsPerPage.value)
        loading.value = false
      }
    }

    const prevPage = async () => {
      if (campaigns.value.pagination.hasPrevPage) {
        loading.value = true
        campaigns.value.pagination.size = itemsPerPage.value
        await campaigns.value.prevPage().catch((error) => {
          alert(error)
        })
        triggerRef(campaigns)
        totalPages.value = Math.ceil(campaigns.value.pagination.totalCount / itemsPerPage.value)
        loading.value = false
      }
    }
    const updateItemsPerPage = async () => {
      loading.value = true
      campaigns.value.pagination.size = itemsPerPage.value
      campaigns.value.pagination.page = 1
      await campaigns.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(campaigns)
      totalPages.value = Math.ceil(campaigns.value.pagination.totalCount / itemsPerPage.value)
      loading.value = false
    }
    const displayStart = computed(() => {
      return (campaigns.value.pagination.page - 1) * itemsPerPage.value + 1
    })

    const displayEnd = computed(() => {
      const end = campaigns.value.pagination.page * itemsPerPage.value
      return end > campaigns.value.pagination.totalCount
        ? campaigns.value.pagination.totalCount
        : end
    })

    return {
      campaigns,
      loading,
      userToken,
      isCanvassingOrgAdmin,
      isCanvassingOrgFieldDirector,
      isGivarooStaff,
      donorFormIsActive,
      //leadFormIsActive,
      locationModalOpen,
      locationModalHeader,
      locationModalSubheader,
      handleDonorFormClick,
      closeLocationModal,
      nextPage,
      prevPage,
      updateItemsPerPage,
      displayEnd,
      displayStart,
      totalPages,
      itemsPerPage,
      campaignColumns,
    }
  },
}
</script>
